import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Settings, RefreshCw, Shield, Network, Server, Copy, CheckCircle, Pencil, X, Check } from 'lucide-react';
import StatusBadge from '../components/common/StatusBadge';
import { useToast } from '../components/common/ToastContainer';
import connectorService from '../services/connector.service';
import type { Connector, ConnectorWireguard, AvailableEdge } from '../services/connector.service';
import { useTenantStore } from '../stores/tenantStore';
import { formatDate } from '../utils/formatDate';
import { useSettingsStore } from '../stores/settingsStore';
import { usePermissions } from '../hooks/usePermissions';

const ConnectorDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const { canUpdate } = usePermissions();
  const [connector, setConnector] = useState<Connector | null>(null);
  const [wireguard, setWireguard] = useState<ConnectorWireguard | null>(null);
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'wireguard'>('overview');
  const [syncing, setSyncing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editingHubEdge, setEditingHubEdge] = useState(false);
  const [availableEdges, setAvailableEdges] = useState<AvailableEdge[]>([]);
  const [selectedHubEdgeId, setSelectedHubEdgeId] = useState('');
  const [savingHubEdge, setSavingHubEdge] = useState(false);

  const fetchConnector = async () => {
    if (!id) return;
    try {
      const data = await connectorService.getById(id);
      setConnector(data);
    } catch (error: any) {
      toast.error('Load Failed', 'Failed to load connector details');
    } finally {
      setLoading(false);
    }
  };

  const fetchWireguard = async () => {
    if (!id) return;
    try {
      const data = await connectorService.getWireguard(id);
      setWireguard(data);
    } catch (error: any) {
      // WireGuard config may not exist yet for pending connectors
      setWireguard(null);
    }
  };

  useEffect(() => {
    fetchConnector();
    fetchWireguard();
  }, [id, selectedTenantId]);

  // Derive online status from wireguard_status enrichment
  useEffect(() => {
    if (!connector) return;
    setIsOnline((connector as any).wireguard_status === 'active');
  }, [connector]);

  const handleSyncConfig = async () => {
    if (!id) return;
    try {
      setSyncing(true);
      const result = await connectorService.syncConfig(id);
      toast.success('Sync Initiated', `Config pushed with ${result.commandCount} commands (seq: ${result.seqNum})`);
      await fetchConnector();
    } catch (error: any) {
      toast.error('Sync Failed', error.message || 'Failed to sync connector config');
    } finally {
      setSyncing(false);
    }
  };

  const handleCopyPublicKey = async () => {
    if (!wireguard?.public_key) return;
    try {
      await navigator.clipboard.writeText(wireguard.public_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copy Failed', 'Failed to copy public key to clipboard');
    }
  };

  const handleEditHubEdge = async () => {
    if (!connector) return;
    setSelectedHubEdgeId(connector.hub_type === 'mtge' ? (connector.hub_mtge_id || '') : (connector.hub_edge_id || ''));
    setEditingHubEdge(true);
    try {
      const edges = await connectorService.getAvailableEdges(connector.tenant_id, connector.id);
      setAvailableEdges(edges);
    } catch {
      setAvailableEdges([]);
    }
  };

  const handleSaveHubEdge = async () => {
    if (!id) return;
    try {
      setSavingHubEdge(true);
      const selectedEdge = availableEdges.find(e => e.id === selectedHubEdgeId);
      await connectorService.update(id, {
        hubEdgeId: selectedHubEdgeId || '',
        hubType: selectedEdge?.type || 'dedicated',
      });
      toast.success('Hub Updated', selectedHubEdgeId ? 'Connector linked to hub' : 'Hub assignment removed');
      setEditingHubEdge(false);
      await fetchConnector();
    } catch (error: any) {
      toast.error('Update Failed', error.message || 'Failed to update hub');
    } finally {
      setSavingHubEdge(false);
    }
  };

  const handleCancelHubEdge = () => {
    setEditingHubEdge(false);
    setSelectedHubEdgeId(
      connector?.hub_type === 'mtge' ? (connector?.hub_mtge_id || '') : (connector?.hub_edge_id || '')
    );
  };

  const getDisplayStatus = (): string => {
    if (!connector) return 'Unknown';
    if (['pending_approval', 'approved', 'provisioned', 'decommissioned'].includes(connector.status)) {
      const labels: Record<string, string> = { pending_approval: 'Pending', approved: 'Approved', provisioned: 'Provisioned', decommissioned: 'Decommissioned' };
      return labels[connector.status] || connector.status;
    }
    return isOnline ? 'Online' : 'Offline';
  };

  const syncStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      synced: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      applying: 'bg-blue-100 text-blue-700',
      failed: 'bg-red-100 text-red-700',
      unknown: 'bg-gray-100 text-gray-600',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status] || colors.unknown}`}>
        {status === 'synced' ? 'Synced' : status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getSyncButtonStyle = () => {
    if (!connector) return 'btn-primary';
    if (connector.sync_status === 'failed') return 'bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors';
    if (connector.is_dirty === 1) return 'bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg font-medium transition-colors';
    return 'btn-primary';
  };

  const getSyncButtonLabel = () => {
    if (syncing) return 'Syncing...';
    if (!connector) return 'Sync Config';
    if (connector.sync_status === 'failed') return 'Retry Sync';
    if (connector.is_dirty === 1) return 'Sync Config (Dirty)';
    return 'Sync Config';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!connector) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Connector not found</p>
        <button onClick={() => navigate('/connectors')} className="btn-primary mt-4">Back to List</button>
      </div>
    );
  }

  const dateSettings = useSettingsStore.getState().getDateFormatSettings();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button onClick={() => navigate('/connectors')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center space-x-3">
              <h1 className="text-3xl font-bold text-gray-900">{connector.connector_name}</h1>
              <StatusBadge status={getDisplayStatus()} />
            </div>
            <div className="flex items-center space-x-4 mt-1">
              <p className="text-gray-600">Serial: {connector.serial_number}</p>
              {connector.deployment_region && (
                <p className="text-gray-500 text-sm">Region: {connector.deployment_region}</p>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {canUpdate && (
            <button
              onClick={handleSyncConfig}
              disabled={syncing}
              className={`${getSyncButtonStyle()} inline-flex items-center space-x-2 disabled:opacity-50`}
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              <span>{getSyncButtonLabel()}</span>
            </button>
          )}
          {canUpdate && (
            <button
              onClick={() => navigate(`/connectors/${id}/config`)}
              className="btn-primary inline-flex items-center space-x-2"
            >
              <Settings className="w-4 h-4" />
              <span>Configure</span>
            </button>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('wireguard')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'wireguard'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Tunnel Status
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Device Info */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <Server className="w-5 h-5 text-primary-600" />
              <span>Device Information</span>
            </h3>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Name</dt>
                <dd className="text-sm font-medium">{connector.connector_name}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Serial Number</dt>
                <dd className="text-sm font-medium font-mono">{connector.serial_number}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Model</dt>
                <dd className="text-sm font-medium">{connector.device_model || '-'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Firmware</dt>
                <dd className="text-sm font-medium">{connector.firmware_version || '-'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Agent Version</dt>
                <dd className="text-sm font-medium font-mono">{connector.agent_version || '-'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Deployment Region</dt>
                <dd className="text-sm font-medium">{connector.deployment_region || '-'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Registered</dt>
                <dd className="text-sm font-medium">{connector.registered_at ? formatDate(connector.registered_at, dateSettings) : '-'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Last Seen</dt>
                <dd className="text-sm font-medium">{connector.last_seen ? formatDate(connector.last_seen, dateSettings) : '-'}</dd>
              </div>
            </dl>
          </div>

          {/* Network */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <Network className="w-5 h-5 text-primary-600" />
              <span>Network</span>
            </h3>
            <div className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 space-y-1">
              <div>
                Connectors pair with a hub (dedicated <strong>Edge</strong> or <strong>MTGE</strong>)
                via the IoT Gateway Server (wg0) backbone tunnel.
              </div>
              <div>
                Transport: <strong>kernel WireGuard</strong> only. For site-to-site IPSec between
                two locations, deploy a dedicated Edge VM and use E2E Peering.
              </div>
            </div>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">WAN IP</dt>
                <dd className="text-sm font-medium font-mono">{connector.wan_ip || '-'}</dd>
              </div>
              <div className="flex justify-between items-center">
                <dt className="text-sm text-gray-500">Hub Edge</dt>
                <dd className="text-sm font-medium">
                  {editingHubEdge ? (
                    <div className="flex items-center space-x-2">
                      <select
                        value={selectedHubEdgeId}
                        onChange={(e) => setSelectedHubEdgeId(e.target.value)}
                        className="input-base text-sm py-1 px-2"
                        disabled={savingHubEdge}
                      >
                        <option value="">None (standalone)</option>
                        {availableEdges.map((edge) => (
                          <option key={edge.id} value={edge.id}>
                            {edge.name || 'Unnamed'} ({edge.type === 'mtge' ? 'MTGE' : 'Edge'})
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={handleSaveHubEdge}
                        disabled={savingHubEdge}
                        className="p-1 hover:bg-green-50 rounded transition-colors"
                        title="Save"
                      >
                        <Check className="w-4 h-4 text-green-600" />
                      </button>
                      <button
                        onClick={handleCancelHubEdge}
                        disabled={savingHubEdge}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title="Cancel"
                      >
                        <X className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      {(connector.hub_edge_id || connector.hub_mtge_id) ? (
                        <button
                          onClick={() => {
                            if (connector.hub_type === 'mtge' && connector.hub_mtge_id) {
                              navigate(`/mtge/${connector.hub_mtge_id}`);
                            } else if (connector.hub_edge_id) {
                              navigate(`/edges/${connector.hub_edge_id}`);
                            }
                          }}
                          className="text-primary-600 hover:text-primary-700 hover:underline"
                        >
                          {connector.hub_edge_name || 'Hub'}{connector.hub_type === 'mtge' ? ' (MTGE)' : ''}
                        </button>
                      ) : (
                        <span className="text-gray-400">No hub assigned</span>
                      )}
                      {canUpdate && (
                        <button
                          onClick={handleEditHubEdge}
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                          title="Change hub edge"
                        >
                          <Pencil className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" />
                        </button>
                      )}
                    </div>
                  )}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Public Key</dt>
                <dd className="text-sm font-medium font-mono flex items-center space-x-2">
                  <span className="truncate max-w-[200px]" title={wireguard?.public_key || '-'}>
                    {wireguard?.public_key || '-'}
                  </span>
                  {wireguard?.public_key && (
                    <button
                      onClick={handleCopyPublicKey}
                      className="p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
                      title="Copy public key"
                    >
                      {copied ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  )}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Listen Port</dt>
                <dd className="text-sm font-medium font-mono">{wireguard?.listen_port || '-'}</dd>
              </div>
              {connector.hub_type === 'mtge' && (
                <div className="flex justify-between items-start">
                  <dt className="text-sm text-gray-500 mt-0.5">App VPN Routes</dt>
                  <dd className="text-sm font-mono text-right">
                    {connector.app_vpn_allowed_ips && connector.app_vpn_allowed_ips.length > 0 ? (
                      <div className="flex flex-col items-end gap-1">
                        {connector.app_vpn_allowed_ips.map((ip) => (
                          <span key={ip} className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-700">{ip}</span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-xs">Not synced — save connector to update</span>
                    )}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Config State */}
          <div className="card p-6 lg:col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <Shield className="w-5 h-5 text-primary-600" />
              <span>Config State</span>
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <div className="text-sm text-gray-500 mb-1">Sync Status</div>
                <div>{syncStatusBadge(connector.sync_status)}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">Sequence Number</div>
                <div className="text-sm font-medium font-mono">{connector.sequence_number}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">Last Pushed</div>
                <div className="text-sm font-medium">{connector.updated_at ? formatDate(connector.updated_at, dateSettings) : '-'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">Last Acked</div>
                <div className="text-sm font-medium">{connector.last_seen ? formatDate(connector.last_seen, dateSettings) : '-'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">Dirty</div>
                <div>
                  {connector.is_dirty === 1 ? (
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">
                      Yes{connector.dirty_reason ? ` - ${connector.dirty_reason}` : ''}
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                      No
                    </span>
                  )}
                </div>
              </div>
            </div>
            {connector.last_error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="text-sm font-medium text-red-700">Last Error</div>
                <div className="text-sm text-red-600 mt-1">{connector.last_error}</div>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* WireGuard Status Tab */
        <div className="space-y-6">
          {wireguard ? (
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                <Shield className="w-5 h-5 text-primary-600" />
                <span>Tunnel Configuration</span>
              </h3>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Interface</dt>
                  <dd className="text-sm font-medium font-mono">{wireguard.interface_name}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Status</dt>
                  <dd><StatusBadge status={wireguard.status} /></dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Listen Port</dt>
                  <dd className="text-sm font-medium font-mono">{wireguard.listen_port}</dd>
                </div>
                <div className="flex justify-between items-center">
                  <dt className="text-sm text-gray-500">Public Key</dt>
                  <dd className="text-sm font-medium font-mono flex items-center space-x-2">
                    <span className="truncate max-w-[400px]" title={wireguard.public_key}>
                      {wireguard.public_key}
                    </span>
                    <button
                      onClick={handleCopyPublicKey}
                      className="p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
                      title="Copy public key"
                    >
                      {copied ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Server Address</dt>
                  <dd className="text-sm font-medium font-mono">{wireguard.server_address}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">MTU</dt>
                  <dd className="text-sm font-medium">{wireguard.mtu}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Endpoint Host</dt>
                  <dd className="text-sm font-medium font-mono">{wireguard.endpoint_host || '-'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Enabled</dt>
                  <dd>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      wireguard.enabled === 1
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {wireguard.enabled === 1 ? 'Yes' : 'No'}
                    </span>
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Created</dt>
                  <dd className="text-sm font-medium">{wireguard.created_at ? formatDate(wireguard.created_at, dateSettings) : '-'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Updated</dt>
                  <dd className="text-sm font-medium">{wireguard.updated_at ? formatDate(wireguard.updated_at, dateSettings) : '-'}</dd>
                </div>
              </dl>
            </div>
          ) : (
            <div className="card p-6 text-center">
              <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600">No tunnel configuration found for this connector.</p>
              <p className="text-gray-400 text-sm mt-1">The tunnel will be configured once the connector is approved and provisioned.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ConnectorDetail;
